import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, ArrowLeft, ArrowRight, Calendar, User } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { blogPosts as mockBlogPosts } from '../mock';
import api from '../services/api';

const BlogPost = () => {
  const { slug } = useParams();
  const [blogPosts, setBlogPosts] = useState(mockBlogPosts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      const data = await api.getBlogPosts(mockBlogPosts);
      if (data.length > 0) setBlogPosts(data);
      setLoading(false);
    };
    loadPosts();
  }, []);

  const post = blogPosts.find(p => p.slug === slug);
  
  if (loading) {
    return (
      <div className="mt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#15448A]"></div>
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="mt-20 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-gray-900">Artigo não encontrado</h1>
          <p className="text-gray-600">O artigo que você procura não existe ou foi removido.</p>
          <Link to="/blog">
            <Button>Voltar para o Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  return (
    <div className="mt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white py-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-6">
            <Link to="/blog" className="inline-flex items-center space-x-2 text-blue-200 hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span>Voltar para o Blog</span>
            </Link>
            
            <Badge className="bg-[#15448A] hover:bg-[#15448A] text-white">
              {post.category}
            </Badge>
            
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-blue-100">
              <div className="flex items-center space-x-2">
                <User size={18} />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar size={18} />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={18} />
                <span>{post.readTime} de leitura</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl mb-12">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-[500px] object-cover"
              />
            </div>
            
            {/* Content */}
            <article className="prose prose-lg max-w-none">
              {post.content.split('\n\n').map((paragraph, index) => {
                // Handle headings
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2 key={index} className="text-3xl font-bold text-gray-900 mt-12 mb-6">
                      {paragraph.replace('## ', '')}
                    </h2>
                  );
                }
                
                // Handle bullet points
                if (paragraph.includes('\n- ') || paragraph.startsWith('- ')) {
                  const items = paragraph.split('\n').filter(item => item.startsWith('- '));
                  return (
                    <ul key={index} className="space-y-3 my-6 ml-6">
                      {items.map((item, i) => (
                        <li key={i} className="text-gray-700 leading-relaxed">
                          {item.replace('- ', '')}
                        </li>
                      ))}
                    </ul>
                  );
                }
                
                // Handle bold text markers
                if (paragraph.startsWith('**') || paragraph.includes(':**')) {
                  return (
                    <p key={index} className="text-gray-700 leading-relaxed my-6 font-semibold">
                      {paragraph.replace(/\*\*/g, '')}
                    </p>
                  );
                }
                
                // Handle special markers
                if (paragraph.startsWith('❌') || paragraph.startsWith('✓')) {
                  return (
                    <p key={index} className="text-gray-700 leading-relaxed my-6 pl-6 border-l-4 border-blue-500">
                      {paragraph}
                    </p>
                  );
                }
                
                // Regular paragraphs
                if (paragraph.trim()) {
                  return (
                    <p key={index} className="text-gray-700 leading-relaxed my-6 text-lg">
                      {paragraph}
                    </p>
                  );
                }
                
                return null;
              })}
            </article>
            
            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags:</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, index) => (
                  <Badge key={index} variant="outline" className="border-blue-300 text-[#15448A]">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Artigos Relacionados</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Card key={relatedPost.id} className="group hover:shadow-xl transition-shadow">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-[#15448A] text-white">
                        {relatedPost.category}
                      </Badge>
                    </div>
                    
                    <CardContent className="p-6 space-y-4">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{relatedPost.date}</span>
                        <span>•</span>
                        <span>{relatedPost.readTime}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#15448A] transition-colors">
                        {relatedPost.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      
                      <Link to={`/blog/${relatedPost.slug}`}>
                        <Button variant="ghost" className="w-full justify-between text-[#15448A] hover:text-[#15448A] hover:bg-[#15448A]/5">
                          Ler artigo
                          <ArrowRight size={18} />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">
              Precisa de Ajuda com Seu Projeto?
            </h2>
            <p className="text-xl text-blue-100">
              Nossa equipe de especialistas está pronta para orientar você e executar seu projeto com excelência.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button size="lg" className="bg-white text-[#15448A] hover:bg-gray-100 shadow-xl">
                  Solicitar Orçamento
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/blog">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                  Ver Mais Artigos
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;